import { useEffect, useState } from "react";
import {
  clearMetrics,
  exportMetricsCsv,
  getMetrics,
  getSummary,
  type Metric,
  type MetricsSummary,
} from "../observability/metrics";

// 開発時計測パネル。?debug=1 でのみ表示。
// 右下フローティング、累計サマリ + 直近 10 件の詳細。

export function MetricsPanel() {
  const [open, setOpen] = useState(true);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [summary, setSummary] = useState<MetricsSummary>({
    count: 0,
    sttCount: 0,
    structureCount: 0,
    ttsCount: 0,
    totalEstimatedCostUsd: 0,
    sttMeanRoundTripMs: 0,
    sttP95RoundTripMs: 0,
    structureMeanRoundTripMs: 0,
    structureP95RoundTripMs: 0,
    ttsMeanRoundTripMs: 0,
    ttsP95RoundTripMs: 0,
  });

  const refresh = () => {
    setMetrics(getMetrics());
    setSummary(getSummary());
  };

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 1000);
    return () => window.clearInterval(id);
  }, []);

  const downloadCsv = () => {
    const csv = exportMetricsCsv();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metrics-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onClear = () => {
    clearMetrics();
    refresh();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="計測パネルを開く"
        className="fixed bottom-3 right-3 z-50 px-2 py-1 text-xs rounded bg-slate-800 text-white shadow"
      >
        📊 metrics ({summary.count})
      </button>
    );
  }

  const recent = metrics.slice(-10).reverse();

  return (
    <aside
      aria-label="計測パネル"
      className="fixed bottom-3 right-3 z-50 w-80 max-h-[60vh] overflow-auto rounded-lg border border-slate-300 bg-white shadow-lg text-xs"
    >
      <header className="flex items-center justify-between p-2 bg-slate-100 border-b border-slate-200">
        <strong>📊 metrics</strong>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={downloadCsv}
            className="px-2 py-0.5 border border-slate-300 rounded hover:bg-white"
          >
            CSV
          </button>
          <button
            type="button"
            onClick={onClear}
            className="px-2 py-0.5 border border-slate-300 rounded hover:bg-white"
          >
            クリア
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="閉じる"
            className="px-2 py-0.5 border border-slate-300 rounded hover:bg-white"
          >
            ×
          </button>
        </div>
      </header>

      <section className="p-2 border-b border-slate-200">
        <div className="grid grid-cols-2 gap-1">
          <Row
            label="件数"
            value={`${summary.count} (STT ${summary.sttCount} / 構造化 ${summary.structureCount})`}
          />
          <Row label="STT mean" value={`${Math.round(summary.sttMeanRoundTripMs)} ms`} />
          <Row label="STT p95" value={`${Math.round(summary.sttP95RoundTripMs)} ms`} />
          <Row label="構造化 mean" value={`${Math.round(summary.structureMeanRoundTripMs)} ms`} />
          <Row label="構造化 p95" value={`${Math.round(summary.structureP95RoundTripMs)} ms`} />
          <Row label="概算コスト" value={`$${summary.totalEstimatedCostUsd.toFixed(5)}`} />
        </div>
        <p className="mt-1 text-[10px] text-slate-500">
          コストは PoC 用の概算（料金変動あり、単価は src/observability/metrics.ts に固定）
        </p>
      </section>

      <section className="p-2">
        <h3 className="font-semibold mb-1">直近 10 件</h3>
        {recent.length === 0 && <p className="text-slate-500">まだ計測なし</p>}
        <ul className="space-y-1">
          {recent.map((m, i) => (
            <li
              key={`${m.recordedAt}-${i}`}
              className="border border-slate-200 rounded p-1 bg-slate-50"
            >
              <div className="flex justify-between">
                <span className="font-mono">
                  {m.kind === "stt"
                    ? "STT"
                    : m.kind === "tts"
                      ? "TTS"
                      : `構造化(${m.questionType})`}
                </span>
                <span>{m.roundTripMs} ms</span>
              </div>
              <div className="text-[10px] text-slate-500">
                {m.kind === "stt"
                  ? `audio=${m.audioDurationMs ?? "?"}ms, chars=${m.chars}`
                  : m.kind === "tts"
                    ? `chars=${m.chars}`
                    : `in=${m.inputTokens} out=${m.outputTokens} total=${m.totalTokens}`}
                {" / "}cost ≈ ${m.estimatedCostUsd.toFixed(6)}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
